/**
 * Track the geographic coordinate under the map cursor.
 *
 * Subscribes to MapLibre's map-level ``mousemove`` and exposes the
 * ``lngLat`` under the pointer for the lower-right coordinate readout
 * (:func:`CursorReadout`). Two behaviours per the in-session §6 decision
 * (2026-05-29):
 *
 * - **rAF-throttled.** ``mousemove`` fires faster than the browser paints;
 *   updates are coalesced to at most one React state update per animation
 *   frame so dragging the map doesn't thrash re-renders.
 * - **Freeze-on-exit.** When the pointer leaves the map surface the last
 *   coordinate is retained (no ``mouseout`` handler), so the readout holds
 *   its last value rather than blanking.
 *
 * Cross-references:
 *   - REQ-UI-002 (MapLibre GL JS map surface).
 *   - docs/engineering/detail/storyboards/flows/03-pan-zoom-cluster/README.md
 *     (the ``.coord-readout`` element).
 */
import { useEffect, useRef, useState } from "react";
import type { MapMouseEvent } from "maplibre-gl";

/** A geographic coordinate under the cursor. */
export interface CursorCoordinates {
  lat: number;
  lng: number;
}

/**
 * Narrow structural subset of ``maplibre-gl``'s ``Map`` this hook needs.
 *
 * Declared explicitly (rather than importing the full ``Map`` type) so the
 * hook is exercised in tests with a lightweight fake that records the
 * ``mousemove`` listener.
 */
export interface CursorMapLike {
  on(type: "mousemove", listener: (event: MapMouseEvent) => void): unknown;
  off(type: "mousemove", listener: (event: MapMouseEvent) => void): unknown;
}

/**
 * Return the coordinate under the map cursor, or ``null`` until the pointer
 * has entered the map at least once.
 *
 * @param map - The loaded map (or ``null`` before MapLibre's ``load``).
 */
export function useCursorCoordinates(map: CursorMapLike | null): CursorCoordinates | null {
  const [coords, setCoords] = useState<CursorCoordinates | null>(null);
  // Coalesce rapid mousemove samples to one state update per frame.
  // ``scheduledRef`` is the guard (reset inside the rAF callback);
  // ``frameRef`` holds the id purely for cancellation on unmount. They are
  // kept separate so the guard never depends on the
  // ``frameRef = requestAnimationFrame(...)`` assignment landing before the
  // callback runs — which it doesn't under a synchronous rAF.
  const scheduledRef = useRef(false);
  const frameRef = useRef<number | null>(null);
  const pendingRef = useRef<CursorCoordinates | null>(null);

  useEffect(() => {
    if (map === null) {
      return undefined;
    }
    const onMove = (event: MapMouseEvent): void => {
      pendingRef.current = { lat: event.lngLat.lat, lng: event.lngLat.lng };
      if (scheduledRef.current) {
        return;
      }
      scheduledRef.current = true;
      frameRef.current = requestAnimationFrame(() => {
        scheduledRef.current = false;
        frameRef.current = null;
        if (pendingRef.current !== null) {
          setCoords(pendingRef.current);
        }
      });
    };
    map.on("mousemove", onMove);
    return () => {
      map.off("mousemove", onMove);
      scheduledRef.current = false;
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [map]);

  return coords;
}
